// User-defined creative context (NeuroGen — NEUROGEN_CONTEXT_SDD).
// Canonical values persisted to the backend are the pt-BR labels (the AI
// pipeline consumes pt-BR); the UI translates the display via labelKey
// (prefix: components.common.creativeContext.).
// --- Briefing de imagem: Uso do estático + cinco classificadores ----------
// specs/uso-do-estatico-ui.md (front) e synapsee_studio_backend/specs/uso-do-estatico.md
// §Catálogo (2026-09-29). Os VALORES são o contrato com o backend (pt-BR
// canônico); `labelKey`/`descriptionKey` são só a exibição.
//
// Uso do estático: enum fechado no servidor (valor fora da lista → 422), por
// isso o valor é um código e não um rótulo. Vazio = "Não definir" (ausência).
export const CONTEXT_STATIC_USE_KV = 'key_visual';
export const CONTEXT_STATIC_USE_AD = 'anuncio';
export const CONTEXT_STATIC_USE_OPTIONS = [
    { value: CONTEXT_STATIC_USE_KV, labelKey: 'options.staticUse.keyVisual', descriptionKey: 'descriptions.staticUse.keyVisual' },
    { value: CONTEXT_STATIC_USE_AD, labelKey: 'options.staticUse.ad', descriptionKey: 'descriptions.staticUse.ad' },
];

// Objetivo da IMAGEM (D10). "Interação / Engajamento" virou "Participação /
// Interação" e "Imagem / Posicionamento" virou "Imagem de marca /
// Posicionamento" — na imagem os valores antigos são mapeados na leitura
// (`normalizeLegacyObjective`). O vídeo tem lista própria
// (CONTEXT_VIDEO_OBJECTIVE_OPTIONS), onde esses valores seguem válidos.
export const CONTEXT_OBJECTIVE_OPTIONS = [
    { value: 'Reconhecimento / Lembrança', labelKey: 'options.objective.awareness', descriptionKey: 'descriptions.objective.awareness' },
    { value: 'Imagem de marca / Posicionamento', labelKey: 'options.objective.brandImage', descriptionKey: 'descriptions.objective.brandImage' },
    { value: 'Interesse / Consideração', labelKey: 'options.objective.consideration', descriptionKey: 'descriptions.objective.consideration' },
    { value: 'Participação / Interação', labelKey: 'options.objective.participation', descriptionKey: 'descriptions.objective.participation' },
    { value: 'Ação / Conversão', labelKey: 'options.objective.conversion', descriptionKey: 'descriptions.objective.conversion' },
];

// Abordagem da peça (coluna nova `context_approach`).
export const CONTEXT_APPROACH_OPTIONS = [
    { value: 'Conceito de marca / Institucional', labelKey: 'options.approach.brandConcept', descriptionKey: 'descriptions.approach.brandConcept' },
    { value: 'Produto / Demonstração', labelKey: 'options.approach.product', descriptionKey: 'descriptions.approach.product' },
    { value: 'Informativo / Educativo', labelKey: 'options.approach.informative', descriptionKey: 'descriptions.approach.informative' },
    { value: 'Depoimento / Prova social', labelKey: 'options.approach.testimonial', descriptionKey: 'descriptions.approach.testimonial' },
    { value: 'Oferta / Chamada', labelKey: 'options.approach.offer', descriptionKey: 'descriptions.approach.offer' },
];

// Canal (coluna nova `context_channels`, LISTA): enum fechado no servidor.
// Múltiplo no KV e no anúncio (0..5; no anúncio, "canal de veiculação
// prioritário" desde 06/10), único com uso vazio (0..1).
export const CONTEXT_CHANNEL_OPTIONS = [
    { value: 'Redes sociais / WhatsApp', labelKey: 'options.channel.social', descriptionKey: 'descriptions.channel.social' },
    { value: 'Sites / Aplicativos', labelKey: 'options.channel.web', descriptionKey: 'descriptions.channel.web' },
    { value: 'Mídia exterior / DOOH', labelKey: 'options.channel.outOfHome', descriptionKey: 'descriptions.channel.outOfHome' },
    { value: 'Impressos / Mídia editorial', labelKey: 'options.channel.print', descriptionKey: 'descriptions.channel.print' },
    { value: 'PDV / Varejo físico', labelKey: 'options.channel.retail', descriptionKey: 'descriptions.channel.retail' },
];

// Presença de CTA (coluna nova `context_cta_presence`, enum fechado). "Sem CTA"
// continua sendo VALOR explícito (≠ "Não definir", que é ausência) e é o
// `CONTEXT_CTA_NONE` que o backend compara literalmente.
export const CONTEXT_CTA_NONE = 'Sem CTA';
export const CONTEXT_CTA_IN_IMAGE = 'CTA na imagem';
export const CONTEXT_CTA_OUTSIDE = 'CTA fora da imagem';
export const CONTEXT_CTA_BOTH = 'CTA na imagem e fora dela';
export const CONTEXT_CTA_PRESENCE_OPTIONS = [
    { value: CONTEXT_CTA_NONE, labelKey: 'options.ctaPresence.none', descriptionKey: 'descriptions.ctaPresence.none' },
    { value: CONTEXT_CTA_IN_IMAGE, labelKey: 'options.ctaPresence.inImage', descriptionKey: 'descriptions.ctaPresence.inImage' },
    { value: CONTEXT_CTA_OUTSIDE, labelKey: 'options.ctaPresence.outside', descriptionKey: 'descriptions.ctaPresence.outside' },
    { value: CONTEXT_CTA_BOTH, labelKey: 'options.ctaPresence.both', descriptionKey: 'descriptions.ctaPresence.both' },
];

// O rótulo do CTA ("Texto do CTA") só vale quando o CTA está na peça (D4):
// com qualquer outra presença o campo some e o valor é limpo.
const _CTA_LABEL_PRESENCES = new Set([CONTEXT_CTA_IN_IMAGE, CONTEXT_CTA_BOTH]);
export const ctaLabelAllowed = (presence) => _CTA_LABEL_PRESENCES.has(presence);

export const CONTEXT_TONE_OPTIONS = [
    { value: 'Direto / Informativo', labelKey: 'options.tone.direct', descriptionKey: 'descriptions.tone.direct' },
    { value: 'Emocional / Inspirador', labelKey: 'options.tone.emotional', descriptionKey: 'descriptions.tone.emotional' },
    { value: 'Leve / Bem-humorado', labelKey: 'options.tone.light', descriptionKey: 'descriptions.tone.light' },
    { value: 'Provocativo / Ousado', labelKey: 'options.tone.provocative', descriptionKey: 'descriptions.tone.provocative' },
    { value: 'Sóbrio / Formal', labelKey: 'options.tone.sober', descriptionKey: 'descriptions.tone.sober' },
];

// --- Rótulo e texto de apoio POR USO (tabela da spec do front) -------------
// Os cinco campos têm as mesmas opções nos dois usos; o que muda é a pergunta.
// Chaves relativas a `components.common.creativeContext.`, como os labelKey.
// 'key_visual' → 'kv' · 'anuncio' → 'ad' · qualquer outra coisa → 'unset'.
export const staticUseCopyGroup = (staticUse) => {
    if (staticUse === CONTEXT_STATIC_USE_KV) return 'kv';
    if (staticUse === CONTEXT_STATIC_USE_AD) return 'ad';
    return 'unset';
};

/**
 * Chaves i18n do rótulo e do texto de apoio de um campo do briefing de imagem.
 * @param {'objective'|'approach'|'channels'|'ctaPresence'|'tone'} field
 * @param {string} staticUse — 'key_visual' | 'anuncio' | '' (ou ausente)
 * @returns {{ labelKey: string, hintKey: string }}
 */
export const fieldCopyKeys = (field, staticUse) => {
    const group = staticUseCopyGroup(staticUse);
    return { labelKey: `byUse.${group}.${field}Label`, hintKey: `byUse.${group}.${field}Hint` };
};

// KV e anúncio aceitam vários canais; o uso vazio aceita um.
export const isMultiChannelUse = (staticUse) => staticUse === CONTEXT_STATIC_USE_KV || staticUse === CONTEXT_STATIC_USE_AD;

// Canal gravado → lista. Aceita a lista, um valor solto (string) ou nada.
export const normalizeChannels = (value) => {
    if (Array.isArray(value)) return value.filter((v) => typeof v === 'string' && v.trim() !== '');
    return typeof value === 'string' && value.trim() !== '' ? [value] : [];
};

/**
 * Canal ao mudar de uso sem trocar KV ↔ anúncio (de/para "sem uso"). Uso vazio
 * aceita um canal só, e nunca se escolhe um arbitrariamente: KV com 2+ canais
 * indo para "sem uso" esvazia o campo.
 * @param {string[]|string|null|undefined} channels — valor atual
 * @param {string} nextUse — uso de destino ('key_visual' | 'anuncio' | '')
 * @returns {string[]}
 */
export const channelsForUse = (channels, nextUse) => {
    const current = normalizeChannels(channels);
    return isMultiChannelUse(nextUse) || current.length < 2 ? current : [];
};

// --- Legado (mapeado na leitura, sem backfill — D6) ------------------------
// Mesmo mapa do backend (`app/neurogen_taxonomy.py`): a função da peça gravada
// antes vira o uso. A abordagem NÃO é derivada (seria inferência, não declaração).
const _LEGACY_STATIC_USE = {
    'Key Visual': CONTEXT_STATIC_USE_KV,
    'KV': CONTEXT_STATIC_USE_KV,
    'Promocional': CONTEXT_STATIC_USE_AD,
    'Performance': CONTEXT_STATIC_USE_AD,
    'Branding': CONTEXT_STATIC_USE_AD,
};
export const legacyStaticUseFromPieceFunction = (pieceFunction) =>
    (typeof pieceFunction === 'string' && _LEGACY_STATIC_USE[pieceFunction.trim()]) || '';

// Objetivos renomeados NA IMAGEM: o valor antigo abre como o novo. Nunca no
// vídeo, cuja lista própria ainda usa os nomes antigos.
const _LEGACY_OBJECTIVE = {
    'Interação / Engajamento': 'Participação / Interação',
    'Imagem / Posicionamento': 'Imagem de marca / Posicionamento',
};
export const normalizeLegacyObjective = (objective) => _LEGACY_OBJECTIVE[objective] ?? objective;

// LEGADO (2026-09-29): a função da peça saiu do formulário, substituída pelo
// Uso do estático + classificadores. A lista fica só para rotular a função
// gravada em foto antiga (cartão de vídeo do modal, via `labelFor`); o mapa
// legado está acima e o PATCH ainda aceita a coluna `context_piece_function`
// (judge harness, clientes antigos).
// Piece function (NEUROGEN_MARKETING_INPUTS_SDD §4.1, D2). Closed enum of 3 —
// no "Outro", because this value routes code (it becomes the plan's category
// and restricts the allowed focus set). Left empty the AI classifies, exactly
// as before (AC#2).
// Taxonomy realigned in NEUROGEN_TAXONOMY_ALIGNMENT §3.1: 'Performance' was the
// semantic opposite of the brand-building piece it named, so it became
// 'Branding' (CTA is irrelevant there) and pure conversion moved into
// 'Promocional'. Order and spelling mirror the backend's PIECE_FUNCTIONS.
export const CONTEXT_PIECE_FUNCTION_OPTIONS = [
    { value: 'Key Visual', labelKey: 'options.pieceFunction.keyVisual' },
    { value: 'Promocional', labelKey: 'options.pieceFunction.promotional' },
    { value: 'Branding', labelKey: 'options.pieceFunction.branding' },
];

// Rótulos sugeridos do CTA ("Texto do CTA", §4.4) — lista do Ads Manager, com
// "Outro…". Desde 2026-09-29 o "Sem CTA" não mora mais aqui: virou a presença
// `CONTEXT_CTA_NONE` (CONTEXT_CTA_PRESENCE_OPTIONS).
export const CONTEXT_CTA_OPTIONS = [
    { value: 'Saiba mais', labelKey: 'options.cta.learnMore' },
    { value: 'Comprar agora', labelKey: 'options.cta.shopNow' },
    { value: 'Cadastre-se', labelKey: 'options.cta.signUp' },
    { value: 'Baixar', labelKey: 'options.cta.download' },
];

// --- Campos de VÍDEO (SYN-2585) -------------------------------------------
// O vídeo NÃO herda a taxonomia de peça estática: `pieceFunction` e `ctaLabel`
// só servem ao NeuroGen de imagem, e mostrá-los no upload de vídeo era o
// "sistema torto" apontado pelo dono do produto (call 11/09). No lugar deles
// entram campos próprios, que o backend grava em `context_video_type`,
// `context_platform` e `context_production_stage`
// (specs/video-insights-parity.md §D1/§D9).
//
// Diferente de `pieceFunction`, estes NÃO roteiam código — são texto para o
// bloco LAW do prompt —, então mantêm o escape "Outro…" (allow-other default).
//
// Revisão 2026-09-24 (taxonomia do dono do produto): as listas foram trocadas
// inteiras e cada opção ganhou uma descrição (o ⓘ da opção no dropdown). Os
// nomes de campo do front `videoType`/`platform` FICAM porque espelham colunas
// que não mudaram de nome: `videoType` agora é a ABORDAGEM do vídeo e
// `platform` o CANAL de veiculação. O CTA saiu do vídeo; no lugar entrou o
// Estágio de Produção (`productionStage`). Valores antigos gravados (ex.:
// 'Tutorial / how-to') seguem válidos e aparecem como texto de "Outro…".
export const CONTEXT_VIDEO_TYPE_OPTIONS = [
    { value: 'Conceito de marca / Institucional', labelKey: 'options.videoType.brandConcept', descriptionKey: 'descriptions.videoType.brandConcept' },
    { value: 'Storytelling / Entretenimento', labelKey: 'options.videoType.storytelling', descriptionKey: 'descriptions.videoType.storytelling' },
    { value: 'Apresentação / Demonstração', labelKey: 'options.videoType.presentation', descriptionKey: 'descriptions.videoType.presentation' },
    { value: 'Depoimento / Prova social', labelKey: 'options.videoType.testimonial', descriptionKey: 'descriptions.videoType.testimonial' },
    { value: 'Oferta / Chamada', labelKey: 'options.videoType.offer', descriptionKey: 'descriptions.videoType.offer' },
];

// Canal de veiculação (grava em `context_platform`).
export const CONTEXT_PLATFORM_OPTIONS = [
    { value: 'Redes sociais', labelKey: 'options.platform.social', descriptionKey: 'descriptions.platform.social' },
    { value: 'YouTube / Vídeo online', labelKey: 'options.platform.onlineVideo', descriptionKey: 'descriptions.platform.onlineVideo' },
    { value: 'TV / TV conectada', labelKey: 'options.platform.tv', descriptionKey: 'descriptions.platform.tv' },
    { value: 'DOOH / Telas em espaços físicos', labelKey: 'options.platform.dooh', descriptionKey: 'descriptions.platform.dooh' },
    { value: 'Site / Landing page', labelKey: 'options.platform.landingPage', descriptionKey: 'descriptions.platform.landingPage' },
];

// Estágio de Produção (2026-09-24) — ocupa no vídeo o lugar que era do CTA.
export const CONTEXT_PRODUCTION_STAGE_OPTIONS = [
    { value: 'Monstro', labelKey: 'options.productionStage.ripomatic', descriptionKey: 'descriptions.productionStage.ripomatic' },
    { value: 'Animatic / Storyboard animado', labelKey: 'options.productionStage.animatic', descriptionKey: 'descriptions.productionStage.animatic' },
    { value: 'Offline / Edição preliminar', labelKey: 'options.productionStage.offline', descriptionKey: 'descriptions.productionStage.offline' },
    { value: 'Filme finalizado', labelKey: 'options.productionStage.finished', descriptionKey: 'descriptions.productionStage.finished' },
];

// Objetivo da peça NO VÍDEO (2026-09-24; hotfix 2026-10-06). Mesmo campo e
// mesma coluna (`objective` → `context_objective`) da imagem, lista PRÓPRIA. O
// backend serve `video_objective` como alias da lista da imagem, por isso o
// front não lê essa chave do catálogo (ver CONTEXT_OPTION_CONFIG_KEYS).
export const CONTEXT_VIDEO_OBJECTIVE_OPTIONS = [
    { value: 'Reconhecimento / Lembrança', labelKey: 'options.videoObjective.awareness', descriptionKey: 'descriptions.videoObjective.awareness' },
    { value: 'Imagem / Posicionamento', labelKey: 'options.videoObjective.image', descriptionKey: 'descriptions.videoObjective.image' },
    { value: 'Interesse / Consideração', labelKey: 'options.videoObjective.consideration', descriptionKey: 'descriptions.videoObjective.consideration' },
    { value: 'Interação / Engajamento', labelKey: 'options.videoObjective.engagement', descriptionKey: 'descriptions.videoObjective.engagement' },
    { value: 'Ação / Conversão', labelKey: 'options.videoObjective.conversion', descriptionKey: 'descriptions.videoObjective.conversion' },
];

// --- Escolha em lista × texto livre (etapa 2 grava conforme se mexe) ---
// A distinção NÃO é por campo, é por VALOR: `objective`, `tone` e `ctaLabel`
// são listas com escape "Outro…", e o que sai desse escape chega tecla a tecla.
// Escolher numa lista é um gesto único e pode gravar na hora; texto tem de
// esperar o cliente parar de digitar, senão vira uma requisição por caractere.
// Limpar um campo ('') também é gesto único, não digitação.
const _CONTEXT_OPTION_VALUES = {
    // O objetivo é o mesmo campo nas duas mídias, com listas diferentes: o
    // conjunto conhece as DUAS, senão o valor de lista de uma mídia passaria por
    // digitação na outra.
    objective: new Set([...CONTEXT_OBJECTIVE_OPTIONS, ...CONTEXT_VIDEO_OBJECTIVE_OPTIONS].map((o) => o.value)),
    tone: new Set(CONTEXT_TONE_OPTIONS.map((o) => o.value)),
    staticUse: new Set(CONTEXT_STATIC_USE_OPTIONS.map((o) => o.value)),
    approach: new Set(CONTEXT_APPROACH_OPTIONS.map((o) => o.value)),
    channels: new Set(CONTEXT_CHANNEL_OPTIONS.map((o) => o.value)),
    ctaPresence: new Set(CONTEXT_CTA_PRESENCE_OPTIONS.map((o) => o.value)),
    pieceFunction: new Set(CONTEXT_PIECE_FUNCTION_OPTIONS.map((o) => o.value)),
    ctaLabel: new Set(CONTEXT_CTA_OPTIONS.map((o) => o.value)),
    // Vídeo (SYN-2585): sem registro aqui, CADA TECLA do escape "Outro…" viraria
    // um PATCH — a etapa 2 grava conforme se mexe e é este mapa que separa
    // "escolheu na lista" (grava na hora) de "está digitando" (espera parar).
    videoType: new Set(CONTEXT_VIDEO_TYPE_OPTIONS.map((o) => o.value)),
    platform: new Set(CONTEXT_PLATFORM_OPTIONS.map((o) => o.value)),
    productionStage: new Set(CONTEXT_PRODUCTION_STAGE_OPTIONS.map((o) => o.value)),
    // `content` é textarea: não tem catálogo, então todo valor dele é digitado.
};

// Lista de canais (chips) é sempre gesto: nunca digitação.
export const isTypedContextValue = (field, value) => {
    if (!value || Array.isArray(value)) return false;
    const known = _CONTEXT_OPTION_VALUES[field];
    return !known || !known.has(value);
};

// Sentinel for the "Outro…" free-text choice (never persisted — the typed
// text is what gets saved). Matches the backend's 60-char column limit.
export const CONTEXT_OTHER = '__other__';
export const CONTEXT_VALUE_MAX = 60;

// --- Catálogo servido pelo backend (config_context_options) ----------------
// As listas acima passam a ser o FALLBACK: em runtime `useContextOptions` lê o
// documento servido por `GET /config/context-options` (arquivo no repo do
// backend, muda por PR + deploy) e só cai nestas constantes sem ele. O que fica
// FIXO, por ter semântica em código: o uso do estático (roteia o planner) e a
// função da peça legada. O "Sem CTA" é valor da presença (`CONTEXT_CTA_NONE`).
// Lista do formulário -> chave do documento de config (nome do backend).
// `videoObjective` (lista do `objective` quando a peça é vídeo) NÃO tem chave:
// o backend serve `video_objective` igual à lista da imagem, e o vídeo usa a
// própria (CONTEXT_VIDEO_OBJECTIVE_OPTIONS, via fallback).
// Desde 2026-09-24 o backend não serve mais `video_type`/`platform`.
// `channels` (campo, lista) lê a chave `channel` do documento.
export const CONTEXT_OPTION_CONFIG_KEYS = {
    objective: 'objective',
    tone: 'tone',
    approach: 'approach',
    channels: 'channel',
    ctaPresence: 'cta_presence',
    ctaLabel: 'cta',
    videoType: 'video_approach',
    platform: 'video_channel',
    productionStage: 'production_stage',
};

// ── Context strength (§9.2) ──────────────────────────────────────────────
// Weights = real impact on the pipeline, never decorative fields. Declared
// heaviest-first: computeContextStrength walks this order, so the missing list
// comes out sorted by weight without a comparator.
// Deviation from §9.2: piece function is SCORED (weight 3) instead of being a
// scoreless "base". The spec assumed it was inherited from the folder and thus
// always present, but it has no folder column (§6.1 — it's per creative), so an
// unscored base would let a piece with no function at all read as "Boa".
// Vídeo (SYN-2585): `videoType` e `platform` entram no lugar de
// `pieceFunction`. As duas mídias nunca pontuam juntas:
// computeContextStrength só soma o `max` das chaves PRESENTES, então cada
// denominador se ajusta sozinho — e os dois têm de dar o MESMO teto, senão a
// mesma barra mediria réguas diferentes.
// 2026-09-15: o `tone` saiu do VÍDEO (não muda o comportamento do agente: o tom
// real ele lê da imagem, da trilha e da fala). O ponto que o tom levava no vídeo
// foi para a `platform` — que é quem dita a leitura inteira da peça —, e não
// para o `videoType`, que só refina dentro dessa condição.
// 2026-09-24: o `ctaLabel` saiu do VÍDEO (a tela não pede mais o CTA) e os 2
// pontos dele foram para o `productionStage`, que entrou no lugar. Na IMAGEM
// nada muda: `ctaLabel` 2 e `tone` 1 continuam valendo.
// 2026-09-29 (uso do estático): a IMAGEM trocou `pieceFunction` e `ctaLabel`
// por uso 3, canal 2, presença de CTA 2 e abordagem 1. O teto da imagem sobe
// para 15; o do vídeo fica em 12. A barra mede % do teto APLICÁVEL, então a
// diferença de teto não distorce o nível.
//   comuns  = brandGuide 3 + objective 2 + content 1                            = 6
//   imagem  = comuns + staticUse 3 + channels 2 + ctaPresence 2 + approach 1
//             + tone 1                                                          = 15
//   vídeo   = comuns + platform 3 + productionStage 2 + videoType 1             = 12
export const CONTEXT_STRENGTH_WEIGHTS = {
    staticUse: 3,
    brandGuide: 3,
    platform: 3,
    objective: 2,
    channels: 2,
    ctaPresence: 2,
    productionStage: 2,
    approach: 1,
    tone: 1,
    videoType: 1,
    content: 1,
};

// Levels: Fraco < 25% · Médio 25–49% · Boa 50–79% · Excelente >= 80% of the
// APPLICABLE max. §9.2 specced 3 absolute cutoffs (<4 / 4-7 / >=8); both changed:
// the 4th tier came from Roberto (03/ago), and absolute cutoffs stopped meaning
// the same thing once each surface scores only its own fields (the upload modal
// tops out at 9 points, the creative sidebar at 12).
export const CONTEXT_STRENGTH_MEDIUM_MIN_PCT = 25;
export const CONTEXT_STRENGTH_GOOD_MIN_PCT = 50;
export const CONTEXT_STRENGTH_GREAT_MIN_PCT = 80;

// How many missing fields the bar surfaces (§9.2: the top 3 by weight).
export const CONTEXT_STRENGTH_MISSING_SHOWN = 3;

// Lista (canais) conta quando tem ao menos um item.
const _isFilled = (value) => (Array.isArray(value)
    ? value.length > 0
    : typeof value === 'string' && value.trim() !== '');

/**
 * Score the declared context and say what's still missing.
 *
 * Only keys PRESENT in `fields` are scored. A key the caller omits is not
 * applicable to that surface (e.g. the upload modal can't read the company
 * brand guide) and leaves both the score and the max — showing it as "missing"
 * there would be a claim we can't back.
 *
 * @param {Object} fields — subset of CONTEXT_STRENGTH_WEIGHTS keys; a present
 *   key that is blank, an empty list or non-string counts as declared-but-empty
 *   (missing).
 * @returns {{ score: number, max: number, pct: number,
 *   level: 'weak'|'medium'|'good'|'great',
 *   missing: Array<{ key: string, weight: number }> }} `missing` holds at most
 *   CONTEXT_STRENGTH_MISSING_SHOWN entries, heaviest first.
 */
export function computeContextStrength(fields = {}) {
    let score = 0;
    let max = 0;
    const missing = [];
    for (const [key, weight] of Object.entries(CONTEXT_STRENGTH_WEIGHTS)) {
        if (!(key in fields)) continue;
        max += weight;
        if (_isFilled(fields[key])) score += weight;
        else missing.push({ key, weight });
    }

    const pct = max === 0 ? 0 : Math.round((score / max) * 100);
    let level = 'weak';
    if (pct >= CONTEXT_STRENGTH_GREAT_MIN_PCT) level = 'great';
    else if (pct >= CONTEXT_STRENGTH_GOOD_MIN_PCT) level = 'good';
    else if (pct >= CONTEXT_STRENGTH_MEDIUM_MIN_PCT) level = 'medium';

    return {
        score,
        max,
        pct,
        level,
        missing: missing.slice(0, CONTEXT_STRENGTH_MISSING_SHOWN),
    };
}

// Campo do formulário -> coluna do `PATCH /experiments/{id}`. Mora aqui porque
// DOIS caminhos gravam o mesmo brief: o wizard do upload (`useMediaUpload`) e a
// retomada pela listagem (`useWizardResume`). Duas cópias divergiriam no dia em
// que um campo novo entrar — e o campo simplesmente não seria gravado por um
// dos dois.
//
// O `PATCH` tem TRÊS estados por campo, não dois (`crud.update_experiment_details`):
// chave AUSENTE = não mexe; string VAZIA = LIMPA (volta a herdar a pasta);
// string com conteúdo = grava. O canal é LISTA: `[]` = limpa, lista = grava.
export const CONTEXT_PATCH_COLUMNS = {
    objective: 'context_objective',
    tone: 'context_tone',
    content: 'context_content',
    staticUse: 'context_static_use',
    approach: 'context_approach',
    channels: 'context_channels',
    ctaPresence: 'context_cta_presence',
    pieceFunction: 'context_piece_function',
    ctaLabel: 'context_cta_label',
    videoType: 'context_video_type',
    platform: 'context_platform',
    productionStage: 'context_production_stage',
};

// Monta o corpo do PATCH de brief de UMA peça a partir dos campos declarados.
// `null`/ausente = o cliente nunca declarou o campo, então nada viaja.
// `''` (ou `[]` no canal) = ele apagou o que tinha, e AÍ viaja (é a instrução
// de limpar). A lista viaja como cópia, para o chamador não compartilhá-la.
export const buildContextPatch = (ctx = {}, extra = {}) => {
    const payload = { ...extra };
    for (const [field, column] of Object.entries(CONTEXT_PATCH_COLUMNS)) {
        const value = ctx[field];
        if (value === undefined || value === null) continue;
        payload[column] = Array.isArray(value) ? [...value] : value;
    }
    return payload;
};
